import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';

// Renderiza a mensagem de um AvisoConsumidor com formatação simples (negrito,
// itálico, listas, links). Usado no Portal do Consumidor (AvisoModal) e na prévia
// do painel de Config (AvisosConsumidorView) — os dois precisam mostrar o texto
// EXATAMENTE igual, senão o adm publica sem saber o que o cliente vai ver.
//
// Sem `rehype-raw` de propósito: HTML dentro da mensagem fica como texto, não vira
// marcação. O texto vem de um campo editável no painel e é exibido para cliente.
//
// `remarkBreaks` é o que mantém compatível com os avisos antigos, escritos como
// texto puro e exibidos com `whitespace-pre-line`: sem ele, markdown junta linhas
// separadas por uma única quebra num parágrafo só e o aviso antigo muda de cara.
//
// Não usa remark-gfm (tabela/strikethrough/autolink) — aviso é texto de comunicado
// curto, e cada extensão a mais é mais chance de um caractere solto do texto antigo
// virar marcação sem querer.

interface MensagemAvisoMarkdownProps {
    value: string;
    /**
     * 'portal' → paleta clara fixa (o Portal do Consumidor não tem tema escuro,
     * mas herda a classe `dark` do html quando o adm usa o app no mesmo navegador).
     * 'admin' → painel de Config, com variantes dark:.
     */
    variante?: 'portal' | 'admin';
}

const MensagemAvisoMarkdown: React.FC<MensagemAvisoMarkdownProps> = ({ value, variante = 'portal' }) => {
    const admin = variante === 'admin';
    const corTexto = admin ? 'text-gray-700 dark:text-gray-300' : 'text-gray-700';
    const corForte = admin ? 'text-gray-900 dark:text-white' : 'text-gray-900';

    return (
        <ReactMarkdown
            remarkPlugins={[remarkBreaks]}
            components={{
                // O preflight do Tailwind zera peso/tamanho de h1-h6 e os marcadores de
                // lista: sem esse mapeamento o markdown "funciona" e não aparece nada.
                p: ({ children }) => <p className={`${corTexto} leading-relaxed mb-3 last:mb-0`}>{children}</p>,
                strong: ({ children }) => <strong className={`font-bold ${corForte}`}>{children}</strong>,
                em: ({ children }) => <em className={`italic ${corTexto}`}>{children}</em>,
                ul: ({ children }) => <ul className={`list-disc pl-5 space-y-1 mb-3 last:mb-0 ${corTexto}`}>{children}</ul>,
                ol: ({ children }) => <ol className={`list-decimal pl-5 space-y-1 mb-3 last:mb-0 ${corTexto}`}>{children}</ol>,
                li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                h1: ({ children }) => <h3 className={`font-bold text-base mt-4 first:mt-0 mb-2 ${corForte}`}>{children}</h3>,
                h2: ({ children }) => <h3 className={`font-bold text-base mt-4 first:mt-0 mb-2 ${corForte}`}>{children}</h3>,
                h3: ({ children }) => <h3 className={`font-semibold mt-3 first:mt-0 mb-1.5 ${corForte}`}>{children}</h3>,
                a: ({ children, href }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary font-medium hover:underline">{children}</a>
                ),
                blockquote: ({ children }) => (
                    <blockquote className={`border-l-4 border-primary/40 pl-3 italic mb-3 last:mb-0 ${corTexto}`}>{children}</blockquote>
                ),
                hr: () => <hr className={`my-4 ${admin ? 'border-gray-200 dark:border-gray-700' : 'border-gray-200'}`} />,
                code: ({ children }) => (
                    <code className={`px-1.5 py-0.5 rounded-md text-[0.9em] font-mono ${admin ? 'bg-gray-100 dark:bg-gray-700 text-primary' : 'bg-gray-100 text-primary'}`}>{children}</code>
                ),
            }}
        >
            {value}
        </ReactMarkdown>
    );
};

export default MensagemAvisoMarkdown;
